import type { Flights, MulticityFlights } from "@b2bportal/air-shopping-api";
import { FlightRatingsEnum } from "@b2bportal/core-types";
import { filterLowestFares } from "@overrides/utilities";

const WESTJET_AIRLINE_CODE = "WS";
const AIR_CANADA_AIRLINE_CODE = "AC";
const PORTER_AIRLINE_CODE = "PD";

/**
 * Carriers whose grid fares are shown unfiltered (all fare classes rather than the 2 cheapest
 * economy fares).
 *
 * WestJet is the control baseline — it was previously gated behind the retired
 * `rbc-westjet-show-all-grid-fares` experiment, whose "available" variation is now the control of
 * `rbc-aircanada-porter-show-all-grid-fares`. The treatment additionally unfilters Air Canada and Porter.
 */
const unfilteredGridCarrierCodes = (
  showAllAirCanadaPorterFares: boolean
): ReadonlySet<string> =>
  showAllAirCanadaPorterFares
    ? new Set([
        WESTJET_AIRLINE_CODE,
        AIR_CANADA_AIRLINE_CODE,
        PORTER_AIRLINE_CODE,
      ])
    : new Set([WESTJET_AIRLINE_CODE]);

export const transformFlightsToFourFareClasses = <
  T extends Flights | MulticityFlights,
>(
  flights: T
): T => {
  const updatedFareSlices = Object.fromEntries(
    Object.entries(flights.fareSlices).map(([fareSliceId, fareSlice]) => {
      const baseFareShelfValue = fareSlice.fareShelf.value;
      const gridFareShelfValue =
        baseFareShelfValue === FlightRatingsEnum.enhanced
          ? FlightRatingsEnum.standard
          : baseFareShelfValue;

      return [
        fareSliceId,
        {
          ...fareSlice,
          fareShelf: { value: gridFareShelfValue },
        },
      ];
    })
  );

  return {
    ...flights,
    fareSlices: updatedFareSlices,
  };
};

export const filterLowestFaresForFixedPricing = (
  flights: Flights,
  showAllAirCanadaPorterFares = false
): Flights => {
  const unfilteredCarriers = unfilteredGridCarrierCodes(
    showAllAirCanadaPorterFares
  );
  const filteredOutbound = flights.outbound.map((slice) => {
    const airline = flights.slices[slice.slice]?.marketingAirline;
    if (airline != null && unfilteredCarriers.has(airline)) {
      return slice;
    }

    return {
      ...slice,
      fares: filterLowestFares(slice.fares, flights),
    };
  });

  return {
    ...flights,
    outbound: filteredOutbound,
  };
};

export const filterLowestFaresForFixedPricingMulticity = (
  flights: MulticityFlights,
  showAllAirCanadaPorterFares = false
): MulticityFlights => {
  const unfilteredCarriers = unfilteredGridCarrierCodes(
    showAllAirCanadaPorterFares
  );
  const filteredDepartures = flights.departures.map((slice) => {
    const airline = flights.slices[slice.slice]?.marketingAirline;
    if (airline != null && unfilteredCarriers.has(airline)) {
      return slice;
    }

    return {
      ...slice,
      fares: filterLowestFares(slice.fares, flights),
    };
  });

  return {
    ...flights,
    departures: filteredDepartures,
  };
};
